"use client";

import React, { useState, useEffect, useRef } from "react";
import { MainLayout } from "@/taskpane/layouts/MainLayout";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import { macros } from "@/constants/macros";
import { Macro } from "@/types/lighthouse";
import { MacroCard } from "@/taskpane/components/MacroCard";

export const Lighthouse = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedMacro, setSelectedMacro] = useState<Macro | null>(macros.length > 3 ? macros[3] : macros[0] || null);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownListRef = useRef<HTMLUListElement | null>(null);

  const recommendedMacros = macros.slice(0, 3);
  const filteredMacros = macros.filter((macro) => macro.name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const element = dropdownListRef.current;
    if (isDropdownOpen && element) {
      const handleWheel = (e: WheelEvent) => {
        const { scrollTop, scrollHeight, clientHeight } = element;

        if (scrollHeight <= clientHeight) {
          e.preventDefault();
          return;
        }

        const atTop = scrollTop === 0;
        const atBottom = scrollHeight - scrollTop <= clientHeight + 1;

        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault();
        }
      };

      element.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        element.removeEventListener("wheel", handleWheel);
      };
    }
  }, [isDropdownOpen]);

  const handleSelectMacro = (macro: Macro) => {
    setSelectedMacro(macro);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  return (
    <MainLayout headerTitle="Lighthouse">
      <div className="p-4 h-[calc(100vh-130px)] overflow-y-auto">
        <h2 className="text-base font-semibold mb-3">All Macros</h2>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full font-semibold flex items-center justify-between text-left p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg"
          >
            <span className={selectedMacro ? "text-white" : "text-white/60"}>
              {selectedMacro ? selectedMacro.name : "Select a macro"}
            </span>
            <FiChevronDown
              className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? "transform rotate-180" : ""}`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute z-20 mt-1 w-full bg-teal-600/15 backdrop-blur-2xl border border-teal-600/30 rounded-lg shadow-xl overflow-hidden">
              <div className="p-2">
                <div className="relative">
                  <FiSearch className="absolute top-1/2 left-2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <input
                    type="text"
                    placeholder="Search macros..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white/10 rounded-md pl-8 pr-2 py-1 text-sm placeholder-white/70 focus:outline-none focus:ring-1 focus:ring-teal-300"
                  />
                </div>
              </div>
              <ul className="max-h-60 overflow-y-auto" ref={dropdownListRef}>
                {filteredMacros.length > 0 ? (
                  filteredMacros.map((macro) => (
                    <li
                      key={macro.id}
                      onClick={() => handleSelectMacro(macro)}
                      className="px-3 py-2 hover:bg-white/10 cursor-pointer"
                    >
                      <h3 className="text-sm font-semibold">{macro.name}</h3>
                      <p className="text-xs text-white/85">{macro.description}</p>
                    </li>
                  ))
                ) : (
                  <li className="px-3 py-2 text-sm text-center text-white/60">No macros found.</li>
                )}
              </ul>
            </div>
          )}
        </div>

        {selectedMacro && (
          <div className="mt-4">
            <MacroCard macro={selectedMacro} isSelected={true} />
          </div>
        )}

        <h2 className="text-base font-semibold mt-6 mb-3">Recommended for you</h2>
        <div className="grid grid-cols-1 gap-4">
          {recommendedMacros.map((macro) => (
            <MacroCard key={macro.id} macro={macro} onSelect={handleSelectMacro} />
          ))}
        </div>
      </div>
    </MainLayout>
  );
};
